import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Avatar,
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFolder } from 'react-icons/fa'
import { pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import { toast } from 'react-toastify'

import CustomChip from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useGetRecipeDetailQuery } from '@/store/apiService'
import {
  clearSelectedRecipeIds,
  setSelectedRecipeIds,
  toggleRecipeSelection,
} from '@/store/project'
import { getRecipeLabelDetails } from '@/utils/helper'

import ProjectFooter from '../projects/ProjectFooter'
import PDFViewerModal from './PDFViewerModal'
import RecipeDetailActionMenu from './RecipeDetailActionMenu'
import RecipeDetailHeader from './RecipeDetailHeader'
import RecipeDetailToolbar from './RecipeDetailToolBar'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

interface RecipeDetailTableProps {
  caseId: string
  caseDetails?: any
}

interface RecipeItem {
  id: number
  name: string
  type: string
  created_ts: string
  created_by: string
  workflow: string
  recipe_pdf: string
}

export default function RecipeDetailTable({
  caseId,
  caseDetails,
}: RecipeDetailTableProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const selectedRows = useAppSelector(
    (state) => state.project.selectedRecipeIds,
  )

  const [offset, setOffset] = useState(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuRowIndex, setMenuRowIndex] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState<string>()
  const [statusFilters, setStatusFilters] = useState<any>()

  const [selectedPdf, setSelectedPdf] = useState<{
    url: string
    name: string
  } | null>(null)

  const limit = 10

  useEffect(() => {
    setOffset(0)
  }, [search, limit, ordering, statusFilters])

  const queryArgs = useMemo(
    () => ({
      caseId,
      limit,
      offset,
      search: search.trim(),
      ordering,
      recipe_type: statusFilters?.join(','),
    }),
    [caseId, limit, offset, search, ordering, statusFilters],
  )

  interface RecipeDetailResponse {
    count: number
    results: RecipeItem[]
  }

  const {
    data: recipeData,
    refetch: refetchRecipe,
    isLoading,
    isFetching,
  } = useGetRecipeDetailQuery(queryArgs) as {
    data: RecipeDetailResponse
    refetch: () => void
    isLoading: boolean
    isFetching: boolean
  }

  useEffect(() => {
    refetchRecipe()
    dispatch(clearSelectedRecipeIds())
  }, [offset, dispatch, refetchRecipe])

  const handlePrev = () => setOffset((prev) => Math.max(prev - limit, 0))
  const handleNext = () => setOffset((prev) => prev + limit)

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl(event.currentTarget)
    setMenuRowIndex(index)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuRowIndex(null)
  }

  const handleCheckboxToggle = (id: number) => {
    dispatch(toggleRecipeSelection(id))
  }

  const allSelected =
    recipeData?.results?.length > 0 &&
    recipeData?.results.every((row: { id: number }) =>
      selectedRows?.includes(row.id),
    )

  const handleSelectAllClick = () => {
    const currentPageIds =
      recipeData?.results?.map((row: { id: number }) => row.id) || []

    if (allSelected) {
      const updatedSelection = selectedRows.filter(
        (id) => !currentPageIds.includes(id),
      )
      dispatch(setSelectedRecipeIds(updatedSelection))
    } else {
      const merged = Array.from(new Set([...selectedRows, ...currentPageIds]))
      dispatch(setSelectedRecipeIds(merged))
    }
  }

  const columnHeaders = [
    t('projectsTable.columns.name'),
    t('projectsTable.columns.type'),
    t('projectsTable.columns.createdAt'),
    t('projectsTable.columns.createdBy'),
  ]

  const containerRef = useRef<HTMLDivElement>(
    null,
  ) as React.RefObject<HTMLDivElement>

  const handleRowClick = (row: RecipeItem) => {
    if (!row?.recipe_pdf) {
      toast.error(t('savedRecipeTab.pdfNotAvailable'))
      return
    }
    setSelectedPdf({ url: row?.recipe_pdf, name: row?.name })
  }

  return (
    <>
      <RecipeDetailToolbar
        selectedRows={selectedRows}
        refetch={refetchRecipe}
        setSearch={setSearch}
        caseId={caseId}
        caseData={
          recipeData?.results?.filter((r: { id: number }) =>
            selectedRows.includes(r.id),
          ) || []
        }
        caseDetails={caseDetails}
      />

      {isLoading || isFetching ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <LoadingSpinner />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: '#1E1E1E', color: 'white' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    sx={checkboxStyle}
                    disableRipple
                    checked={allSelected}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                {columnHeaders.map((label) => (
                  <TableCell key={label}>
                    <RecipeDetailHeader
                      label={label}
                      filters={statusFilters}
                      setFilters={setStatusFilters}
                      ordering={ordering}
                      setOrdering={setOrdering}
                      caseId={caseId}
                    />
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {recipeData?.results?.map((row: RecipeItem, idx: number) => {
                const isChecked = selectedRows.includes(row.id)

                const { workflowColor, labelText } = getRecipeLabelDetails({
                  workflow: row.workflow,
                  type: row.type,
                })

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': { bgcolor: '#272727' },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        sx={checkboxStyle}
                        checked={isChecked}
                        onChange={() => handleCheckboxToggle(row?.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ ...textStyle, whiteSpace: 'nowrap' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Avatar
                          variant="square"
                          sx={{
                            borderRadius: '8px',
                            height: '60px',
                            width: '60px',
                            background: 'rgba(189, 189, 189, 1)',
                            cursor: 'pointer',
                          }}
                        >
                          <FaFolder size="20px" color="white" />
                        </Avatar>
                        <Typography sx={{ px: 2 }}>{row.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={textStyle}>
                      <CustomChip
                        label={labelText}
                        size="medium"
                        sx={{
                          bgcolor: workflowColor,
                          color: 'white',
                          '&:hover': { bgcolor: workflowColor },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={textStyle}>
                      {moment
                        .utc(row.created_ts)
                        .format('MMM Do, YYYY, h:mmA [UTC]')}
                    </TableCell>
                    <TableCell sx={textStyle}>{row.created_by}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        sx={{ bgcolor: '#76768033', padding: '4px 8px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleMenuOpen(e, idx)
                        }}
                      >
                        <MoreHorizIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>

            <ProjectFooter
              page={Math.floor(offset / limit)}
              rowsPerPage={limit}
              count={recipeData?.count || 0}
              handlePrev={handlePrev}
              handleNext={handleNext}
            />
          </Table>
        </TableContainer>
      )}

      <RecipeDetailActionMenu
        anchorEl={anchorEl}
        handleMenuClose={handleMenuClose}
        caseData={
          menuRowIndex !== null ? recipeData?.results[menuRowIndex] : null
        }
        refetch={refetchRecipe}
        caseDetails={caseDetails}
      />

      {selectedPdf && (
        <PDFViewerModal
          open={!!selectedPdf}
          pdfUrl={selectedPdf?.url || ''}
          pdfName={selectedPdf?.name || ''}
          onClose={() => setSelectedPdf(null)}
          containerRef={containerRef}
        />
      )}
    </>
  )
}

const textStyle = {
  color: 'white',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '143%',
  letterSpacing: '0.17px',
}

const checkboxStyle = {
  color: 'white',
  '&.Mui-checked': { color: '#90caf9' },
  '& .MuiSvgIcon-root': { fontSize: 20 },
}
